import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SAFETY_MEASURE_ITEMS,
  allowedVoltageClasses,
  SAFETY_APPROVAL_ATTACHMENT_MIME_TYPES,
  effectiveExcavationInProtectedZone,
  effectiveSafetyApproval,
  effectiveVoltageClass,
  emptySafetyApprovalDraft,
  emptySafetyMeasureDrafts,
  excavationInProtectedZoneWaived,
  proposedExcavationInProtectedZone,
  proposedSafetyApproval,
  proposedVoltageClass,
  reconcileObserver,
  reconcileServiceOrgPresenceText,
  safetyApprovalWaived,
  SERVICE_ORG_PRESENCE_ITEM,
  serviceOrgPresenceRequirement,
  voltageClassFromWorkType,
  type SafetyApprovalDraft,
  type SafetyMeasureItemNumber,
  CoreCrewReadinessRefusalSchema,
} from '@railroute/contracts/core';
import {
  ApiError,
  createWorkOrder,
  fetchCrewCandidates,
  fetchRequirementMatrixRow,
  fetchWorkSites,
  fetchWorkTypes,
  uploadSafetyApprovalAttachment,
  type ElectricalGroup,
  type InstallationVoltageClass,
  type SubdivisionMember,
  type WorkRequirementMatrixRow,
} from '../api';
import {
  assignablePeople,
  buildCreateWorkOrderDto,
  crewAdmissionLabel,
  draftIsComplete,
  keepAssignable,
  type IssueDraft,
} from '../work-orders/issue-draft';
import { blockingCrew, crewDetails, crewStatus, filterCrew, refusalByWorker } from '../work-orders/crew-readiness';
import { useResource } from './useResource';

// Выписка наряда за столом (issue #446).
//
// Набор мероприятий по пунктам 5.1–5.10 бланка и состав бригады — работа за
// компьютером, а не пальцем в телефоне. Экран тот же по существу, что в
// приложении, только развёрнутый: всё видно сразу, без модальных окон и
// прокрутки по одному полю.
//
// НИ ОДНОЙ ПРОВЕРКИ ПО СУЩЕСТВУ ЗДЕСЬ НЕТ. Требуемая группа, состав по
// матрице, наблюдающий, напряжение, согласование по пункту 8, полномочия
// названных лиц — считает и отвергает СЕРВЕР, и его отказ показывается
// словами (`serverRefusal`) вверху формы, с прокруткой к нему. Готовность
// бригады панель показывает заранее, но по ответу того же сервера
// (`crew-readiness.ts`) и только подсказкой: кнопку выписки она не запирает.
//
// ТОЛЬКО ВЫПИСКА. Допуск, целевой инструктаж, старт, продление и закрытие
// происходят на месте работ, у оборудования, и переносить их за стол нельзя
// по смыслу.

const GROUPS: ElectricalGroup[] = ['II', 'III', 'IV', 'V'];

// Напряжение электроустановки (issue #463). Порядок как в таблице Правил
// № 222 — сначала выше 1000 В, потом до 1000 В.
const VOLTAGE_CLASSES: { value: InstallationVoltageClass; label: string }[] = [
  { value: 'ABOVE_1000V', label: 'выше 1000 В' },
  { value: 'UP_TO_1000V', label: 'до 1000 В' },
];

const voltageLabel = (voltage: InstallationVoltageClass | null): string =>
  VOLTAGE_CLASSES.find((option) => option.value === voltage)?.label ?? 'не указано';

type MatrixState = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export function WorkOrderIssueSection({
  token,
  organizationId,
  subdivisionId,
  onUnauthorized,
}: {
  token: string;
  organizationId: string;
  /** Подразделение выдающего из его членства. `null` — выписывать некуда. */
  subdivisionId: string | null;
  onUnauthorized: () => void;
}) {
  const workTypes = useResource(() => fetchWorkTypes(token, organizationId), onUnauthorized, [token, organizationId]);
  const workSites = useResource(() => fetchWorkSites(token, organizationId), onUnauthorized, [token, organizationId]);
  const crew = useResource(() => fetchCrewCandidates(token, organizationId), onUnauthorized, [token, organizationId]);

  const [draft, setDraft] = useState<IssueDraft>(() => emptyDraft(subdivisionId));
  const [matrixRow, setMatrixRow] = useState<WorkRequirementMatrixRow | null>(null);
  const [matrixState, setMatrixState] = useState<MatrixState>('idle');
  const matrixRequest = useRef(0);
  const [serverRefusal, setServerRefusal] = useState<string | null>(null);
  // Отказ «Бригада не готова» поимённо: кого подсветить в списке и почему.
  const [crewRefusal, setCrewRefusal] = useState<Map<string, string | null>>(() => new Map());
  const [crewQuery, setCrewQuery] = useState('');
  // Отказ в приложении файла — у поля файла той строки согласования, а не
  // вверху: наряд ещё не выписывали, и «Наряд не выписан» было бы неправдой.
  const [attachmentError, setAttachmentError] = useState<{ index: number; message: string } | null>(null);
  const [openedMemberId, setOpenedMemberId] = useState<string | null>(null);
  // Отказ появлялся в самом низу страницы, под кнопкой, и выдающий мог его
  // не заметить (владелец, 27.09.2026). Теперь он вверху формы, и экран сам
  // прокручивается к нему.
  const refusalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (serverRefusal !== null) refusalRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [serverRefusal]);
  const [issuedNumber, setIssuedNumber] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Смена организации меняет и подразделение выдающего: черновик прошлой
  // организации в новой не годится ничем — ни людьми, ни видом работы.
  useEffect(() => {
    setDraft(emptyDraft(subdivisionId));
    setMatrixRow(null);
    setMatrixState('idle');
    setServerRefusal(null);
    setCrewRefusal(new Map());
    setIssuedNumber(null);
  }, [organizationId, subdivisionId]);

  const patch = useCallback((change: Partial<IssueDraft>) => {
    setDraft((prev) => ({ ...prev, ...change }));
  }, []);

  // Наблюдающий, убранный из бригады, сбрасывается.
  useEffect(() => {
    setDraft((prev) => {
      const observerId = reconcileObserver(prev.memberIds, prev.observerId);
      return observerId === prev.observerId ? prev : { ...prev, observerId };
    });
  }, [draft.memberIds]);

  const people = useMemo(() => crew.data ?? [], [crew.data]);
  const selectedWorkType = (workTypes.data ?? []).find((type) => type.id === draft.workTypeCatalogId) ?? null;

  // Вид работы выбран — подгружаем строку матрицы, чтобы ПОКАЗАТЬ требования.
  // Это превью, а не ворота: применяет требования сервер.
  useEffect(() => {
    const requestId = ++matrixRequest.current;
    setMatrixRow(null);
    if (draft.workTypeCatalogId === null) {
      setMatrixState('idle');
      return;
    }
    setMatrixState('loading');
    fetchRequirementMatrixRow(draft.workTypeCatalogId, token, organizationId).then(
      (row) => {
        if (requestId !== matrixRequest.current) return;
        setMatrixRow(row);
        setMatrixState(row === null ? 'missing' : 'ready');
      },
      (error: unknown) => {
        if (requestId !== matrixRequest.current) return;
        if (error instanceof ApiError && error.status === 401) onUnauthorized();
        setMatrixState('error');
      },
    );
  }, [draft.workTypeCatalogId, token, organizationId, onUnauthorized]);

  const requiresObserver = matrixState === 'ready' && matrixRow?.requiresObserver === true;
  // Issue #470 (B-089): присутствие представителя обслуживающей организации на
  // месте работ — Правила № 222, пункты 302, 431, 527, 537. Мероприятие
  // подставляется в графу 5.10 сразу, а не только сервером при выписке: иначе
  // выдающему нечего назначать исполнителя и нечего читать перед подписью.
  // Слова у панели и у сервера одни — `reconcileServiceOrgPresenceText`.
  const servicePresence = serviceOrgPresenceRequirement(matrixState === 'ready' ? matrixRow : null);
  const servicePresenceParty = servicePresence?.party ?? null;
  // Умолчание напряжения по виду работ. Строка матрицы не загрузилась —
  // умолчания нет, и поле показывает «не указано»: подставлять сюда догадку
  // значило бы подписать напряжение, которого никто не читал.
  const proposedVoltage = proposedVoltageClass(matrixState === 'ready' ? matrixRow : null);
  const shownVoltage = effectiveVoltageClass(draft.voltageClassDecision, proposedVoltage);
  const voltageFromWorkType = voltageClassFromWorkType(draft.voltageClassDecision, proposedVoltage);
  const proposedApproval = proposedSafetyApproval(selectedWorkType);
  const approvalChecked = effectiveSafetyApproval(draft.safetyApprovalDecision, proposedApproval);
  const approvalWaived = safetyApprovalWaived(draft.safetyApprovalDecision, proposedApproval);
  // Issue #469: земляные работы в охранной зоне подземных коммуникаций. Тот же
  // порядок «справочник предлагает — выдающий решает», что у согласования.
  const proposedExcavation = proposedExcavationInProtectedZone(selectedWorkType);
  const excavationChecked = effectiveExcavationInProtectedZone(
    draft.excavationDecision,
    proposedExcavation,
  );
  const excavationWaived = excavationInProtectedZoneWaived(
    draft.excavationDecision,
    proposedExcavation,
  );
  const complete = draftIsComplete(draft, requiresObserver, proposedVoltage);
  // Группу задаёт вид работ, если у него есть строка требований: её же
  // проверяет допуск, её же сервер пишет в бланк и в подписываемый документ.
  // Одно поле — одно значение (решение владельца 2026-09-25).
  const groupFromWorkType = matrixState === 'ready' && matrixRow !== null;
  // Группа, с которой сравнивается бригада в списке: та же, что уйдёт в
  // наряд, — из вида работ, а без его требований — выбранная в форме.
  const crewRequiredGroup: ElectricalGroup | null = groupFromWorkType
    ? matrixRow?.requiredElectricalGroup ?? null
    : draft.requiredElectricalGroup;
  const shownCrew = useMemo(() => filterCrew(people, crewQuery), [people, crewQuery]);
  // Кто из выбранных мешает выписке — видно у кнопки ДО нажатия.
  const blocking = useMemo(
    () => blockingCrew(people, draft.memberIds, crewRequiredGroup),
    [people, draft.memberIds, crewRequiredGroup],
  );

  // Списки ответственных — только те, кого на роль можно назначить при
  // выбранном напряжении (ответ сервера, см. `assignablePeople`).
  const responsibleOptions = useMemo(() => assignablePeople(people, 'RESPONSIBLE', shownVoltage), [people, shownVoltage]);
  const admittingOptions = useMemo(() => assignablePeople(people, 'ADMITTING', shownVoltage), [people, shownVoltage]);
  const producerOptions = useMemo(() => assignablePeople(people, 'PRODUCER', shownVoltage), [people, shownVoltage]);
  const observerOptions = useMemo(
    () => assignablePeople(people, 'OBSERVER', shownVoltage).filter((member) => draft.memberIds.includes(member.id)),
    [people, shownVoltage, draft.memberIds],
  );

  // Выбранный, которого на роль больше назначить нельзя, сбрасывается.
  useEffect(() => {
    setDraft((prev) => {
      const next = {
        supervisorId: keepAssignable(prev.supervisorId, responsibleOptions),
        admittingUserId: keepAssignable(prev.admittingUserId, admittingOptions),
        producerId: keepAssignable(prev.producerId, producerOptions),
        observerId: keepAssignable(prev.observerId, observerOptions),
      };
      return next.supervisorId === prev.supervisorId &&
        next.admittingUserId === prev.admittingUserId &&
        next.producerId === prev.producerId &&
        next.observerId === prev.observerId
        ? prev
        : { ...prev, ...next };
    });
  }, [responsibleOptions, admittingOptions, producerOptions, observerOptions]);

  const toggleMember = (id: string) => {
    // Убранный из бригады больше не подсвечивается прежним отказом.
    setCrewRefusal((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    setDraft((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(id)
        ? prev.memberIds.filter((member) => member !== id)
        : [...prev.memberIds, id],
    }));
  };

  const setMeasure = (itemNumber: SafetyMeasureItemNumber, change: { content?: string; executorUserId?: string | null }) =>
    setDraft((prev) => ({
      ...prev,
      measures: { ...prev.measures, [itemNumber]: { ...prev.measures[itemNumber], ...change } },
    }));

  // #470: мероприятие о присутствии представителя — в графу 5.10, как только
  // вид работ его потребовал, и вон из неё, когда вид работ сменился на тот,
  // которому оно не нужно. Вписанное выдающим остаётся: убирается ровно строка
  // прежнего мероприятия.
  const presencePartyRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = presencePartyRef.current;
    if (previous === servicePresenceParty) return;
    presencePartyRef.current = servicePresenceParty;
    setDraft((prev) => {
      const measure = prev.measures[SERVICE_ORG_PRESENCE_ITEM];
      const content = reconcileServiceOrgPresenceText(measure.content, previous, servicePresenceParty);
      if (content === measure.content) return prev;
      return {
        ...prev,
        measures: { ...prev.measures, [SERVICE_ORG_PRESENCE_ITEM]: { ...measure, content } },
      };
    });
  }, [servicePresenceParty]);

  // Issue #468: строки согласования по пункту 8. Добавляются и убираются по
  // одной — работа у автодороги и у кабеля связи разом даёт две организации.
  const setApproval = (index: number, change: Partial<SafetyApprovalDraft>) =>
    setDraft((prev) => ({
      ...prev,
      safetyApprovals: prev.safetyApprovals.map((row, i) => (i === index ? { ...row, ...change } : row)),
    }));
  const addApproval = () =>
    setDraft((prev) => ({ ...prev, safetyApprovals: [...prev.safetyApprovals, emptySafetyApprovalDraft()] }));
  // Последняя строка не удаляется, а очищается: блоку нужна хотя бы одна
  // строка, иначе выдающему нечего заполнять, кроме кнопки «добавить».
  // Issue #469: приложение к согласованию. Файл уходит на сервер СРАЗУ при
  // выборе, а не вместе с выпиской: наряд выписывается одним запросом, и
  // десять мегабайт в его теле означали бы, что каждая неудачная попытка гонит
  // их заново. В черновике остаются реквизиты, которые видит человек.
  //
  // Проверок по существу здесь нет: размер, тип и целостность отвергает сервер,
  // и его отказ показывается словами у поля файла (`attachmentError`).
  const attachFile = async (index: number, file: File) => {
    setBusy(true);
    setAttachmentError(null);
    setServerRefusal(null);
    try {
      const contentBase64 = await readFileAsBase64(file);
      const uploaded = await uploadSafetyApprovalAttachment(
        { fileName: file.name, mimeType: file.type, contentBase64 },
        token,
        organizationId,
      );
      setDraft((prev) => ({
        ...prev,
        safetyApprovals: prev.safetyApprovals.map((row, i) =>
          i === index ? { ...row, attachments: [...row.attachments, uploaded] } : row,
        ),
      }));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) onUnauthorized();
      setAttachmentError({ index, message: error instanceof Error ? error.message : 'Файл не приложен' });
    } finally {
      setBusy(false);
    }
  };

  // Убирается только из ЧЕРНОВИКА: наряда ещё нет, и удалять на сервере нечего.
  // Приложение выписанного наряда не убирается вовсе — оно часть документа.
  const detachFile = (index: number, attachmentId: string) =>
    setDraft((prev) => ({
      ...prev,
      safetyApprovals: prev.safetyApprovals.map((row, i) =>
        i === index
          ? { ...row, attachments: row.attachments.filter((a) => a.id !== attachmentId) }
          : row,
      ),
    }));

  const removeApproval = (index: number) =>
    setDraft((prev) => ({
      ...prev,
      safetyApprovals:
        prev.safetyApprovals.length === 1
          ? [emptySafetyApprovalDraft()]
          : prev.safetyApprovals.filter((_, i) => i !== index),
    }));

  const issue = async () => {
    if (!complete || busy) return;
    setBusy(true);
    setServerRefusal(null);
    setCrewRefusal(new Map());
    setIssuedNumber(null);
    try {
      const created = await createWorkOrder(
        buildCreateWorkOrderDto(draft, proposedVoltage, groupFromWorkType),
        token,
        organizationId,
      );
      setIssuedNumber(created.number);
      setDraft(emptyDraft(subdivisionId));
    } catch (error) {
      // Сервер называет человека и роль, вид работы и требование, номер
      // наряда, который уже занят. Этот текст и нужен выдающему: по коду 400
      // он не поймёт, что в форме менять.
      if (error instanceof ApiError && error.status === 401) onUnauthorized();
      const message = error instanceof Error ? error.message : 'Наряд не выписан';
      // «Бригада не готова» приходит с поимённым перечнем — по нему неготовые
      // подсвечиваются прямо в списке бригады, с причиной у каждого.
      const crew = error instanceof ApiError
        ? CoreCrewReadinessRefusalSchema.safeParse((error.body as { crew?: unknown } | null)?.crew)
        : null;
      setCrewRefusal(crew?.success ? refusalByWorker(message, crew.data) : new Map());
      setServerRefusal(message);
    } finally {
      setBusy(false);
    }
  };

  if (subdivisionId === null) {
    return (
      <p className="section__note">
        Вы не приписаны к подразделению, поэтому выписать наряд не из чего: наряд выдаётся в подразделение.
        Приписку ведёт администратор организации.
      </p>
    );
  }

  return (
    <div className="issue screen-only">
      {/* Отказ — вверху, куда экран прокручивается сам: внизу, под кнопкой,
          его не замечали. */}
      <div ref={refusalRef}>
        {serverRefusal !== null ? (
          <div className="ui-error panel__error issue__refusal" role="alert">
            <strong>Наряд не выписан.</strong>
            <span className="issue__refusal-text">{serverRefusal}</span>
            {crewRefusal.size > 0 ? (
              <span>Неготовые работники подсвечены в «Составе бригады».</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="issue__grid">
        <section className="issue__block">
          <h3 className="issue__title">Наряд</h3>

          {/* Номер наряда не вводится: его ставит система при выдаче по
              порядку журнала организации (решение владельца 25.09.2026). */}
          <p className="issue__hint">Номер наряда присвоит система при выдаче — по порядку журнала учёта.</p>

          <label className="issue__field">
            <span className="issue__label">Вид работы из справочника</span>
            {/* Закрытый список обрезает длинное имя вида работ, а отличающая
                часть — уточнение в скобках в конце. Полное имя — во всплывающей
                подсказке и в поле «Вид работ» ниже, куда оно подставляется. */}
            <select
              className="ui-input"
              title={selectedWorkType?.name ?? undefined}
              value={draft.workTypeCatalogId ?? ''}
              onChange={(event) => {
                const next = (workTypes.data ?? []).find((type) => type.id === event.target.value) ?? null;
                patch({
                  workTypeCatalogId: next?.id ?? null,
                  // Свободный текст подставляется наименованием, если пуст или
                  // повторял прежний выбор: он остаётся в бланке и на его
                  // байтах стоят выпущенные подписи.
                  workType:
                    next && (draft.workType.trim().length === 0 || draft.workType === selectedWorkType?.name)
                      ? next.name
                      : draft.workType,
                });
              }}
              disabled={busy}
            >
              <option value="">Не выбран</option>
              {(workTypes.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
          {workTypes.error ? (
            <p className="section__note">Справочник видов работ не загрузился — наряд выписывается свободным текстом.</p>
          ) : null}
          {draft.workTypeCatalogId === null ? null : (
            <RequirementsPreview state={matrixState} row={matrixRow} />
          )}

          <label className="issue__field">
            <span className="issue__label">Вид работ</span>
            <textarea
              className="ui-input issue__area"
              value={draft.workType}
              onChange={(event) => patch({ workType: event.target.value })}
              disabled={busy}
            />
          </label>

          <label className="issue__field">
            <span className="issue__label">Меры безопасности</span>
            <textarea
              className="ui-input issue__area"
              value={draft.safetyMeasures}
              onChange={(event) => patch({ safetyMeasures: event.target.value })}
              disabled={busy}
            />
          </label>

          <label className="issue__field">
            <span className="issue__label">Место работ</span>
            <select
              className="ui-input"
              value={draft.workSiteId ?? ''}
              onChange={(event) => patch({ workSiteId: event.target.value === '' ? null : event.target.value })}
              disabled={busy}
            >
              <option value="">Не выбрано</option>
              {(workSites.data ?? []).map((site) => (
                <option key={site.id} value={site.id}>
                  {site.kind === 'SECTION' ? `${site.name} · перегон` : site.name}
                </option>
              ))}
            </select>
          </label>

          <label className="issue__field">
            <span className="issue__label">Уточнение места (км, пикет)</span>
            <input
              className="ui-input"
              value={draft.workSiteDetail}
              onChange={(event) => patch({ workSiteDetail: event.target.value })}
              disabled={busy}
            />
          </label>

          {/* Одно поле группы (решение владельца 2026-09-25). У вида работ
              есть требования — группу задают они, и она же стоит в бланке,
              в подписи и в проверке допуска; выбрать другую нельзя. Вида
              работ нет или требований у него нет — группу называет наряд,
              и проверяет допуск именно её. */}
          {groupFromWorkType && matrixRow ? (
            <div className="issue__field">
              <span className="issue__label">Требуемая группа по электробезопасности</span>
              <span>
                {matrixRow.requiredElectricalGroup ?? 'не требуется'} — по виду работ
              </span>
            </div>
          ) : (
            <label className="issue__field">
              <span className="issue__label">Требуемая группа по электробезопасности</span>
              <select
                className="ui-input"
                value={draft.requiredElectricalGroup ?? ''}
                onChange={(event) =>
                  patch({
                    requiredElectricalGroup: event.target.value === '' ? null : (event.target.value as ElectricalGroup),
                  })
                }
                disabled={busy || matrixState === 'loading'}
              >
                <option value="">Не указана</option>
                {GROUPS.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Напряжение электроустановки (issue #463, карточка B-075).
              Стоит сразу под требуемой группой намеренно: по пунктам 33, 35,
              37 и 38 Правил № 222 порог группы ответственных лиц выводится
              именно из напряжения.

              Поле заполнено умолчанием вида работ и подписано «по виду
              работ» — выдающий обязан видеть, что значение подставлено, а не
              выбрано им: удостоверяет он подписью и то, и другое одинаково
              (ADR-004 §13).

              Issue #464 (карточка B-062): поле обязательное — наряд без
              напряжения сервер не выписывает. Поэтому «не указано» выбором
              больше не является: пустая строка осталась только как состояние
              незаполненного поля (вид работ без умолчания либо вид работ не
              выбран) и обратно не выбирается. Решает всё равно сервер, здесь
              только незаполненное поле названо вслух — иначе выдающий узнавал
              бы о нём после заполнения всей формы. */}
          <label className="issue__field">
            <span className="issue__label">Напряжение электроустановки</span>
            <select
              className="ui-input"
              value={shownVoltage ?? ''}
              onChange={(event) =>
                patch({
                  voltageClassDecision:
                    event.target.value === '' ? null : (event.target.value as InstallationVoltageClass),
                })
              }
              disabled={busy}
            >
              <option value="" disabled>
                Не выбрано
              </option>
              {/* Вид работ — нижняя планка (решение владельца 25.09.2026,
                  как с группой ЭБ): «выше 1000 В» понизить нельзя. Сервер
                  отвергает понижение сам; здесь его не предлагают. */}
              {VOLTAGE_CLASSES.filter((option) => allowedVoltageClasses(proposedVoltage).includes(option.value)).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {voltageFromWorkType ? (
              <span className="issue__hint">
                По виду работ: {voltageLabel(proposedVoltage)}.{' '}
                {proposedVoltage === 'ABOVE_1000V' ? 'Понизить нельзя.' : 'Можно повысить до «выше 1000 В».'}
              </span>
            ) : null}
            {shownVoltage === null ? (
              <span className="issue__hint">
                Без напряжения наряд не выписывается — выберите «выше 1000 В» или «до 1000 В».
              </span>
            ) : null}
          </label>

          {/* Пункт 8 Правил № 344: справочник ПРЕДЛАГАЕТ, выдающий
              подтверждает или снимает. Снятие не запрещается — по пункту 19
              меры безопасности определяет выдающий, и запрет переложил бы его
              ответственность на справочник. Снятие записывает сервер вместе с
              тем, кто снял и когда. */}
          <label className="issue__check">
            <input
              type="checkbox"
              checked={approvalChecked}
              onChange={() => patch({ safetyApprovalDecision: !approvalChecked })}
              disabled={busy}
            />
            <span>
              Работы согласованы с организациями, обслуживающими коммуникации
              <span className="issue__hint">
                Пункт 8 Правил № 344: работы вблизи действующих дорог, ЛЭП, газо- и теплопроводов, а также все
                земляные работы.
              </span>
            </span>
          </label>
          {proposedApproval === true && !approvalWaived ? (
            <p className="section__note">Справочник видов работ считает согласование обязательным для этой работы.</p>
          ) : null}
          {approvalWaived ? (
            <p className="section__note section__note--warn">
              Вы снимаете согласование, которое предложил справочник. Это будет записано в наряде вместе с вашим
              именем.
            </p>
          ) : null}

          {/* Issue #469 (B-034, шаг 3): земляные работы в охранной зоне
              подземных коммуникаций. Тот же порядок, что у согласования выше:
              справочник ПРЕДЛАГАЕТ, выдающий подтверждает или снимает —
              справочник знает вид работ, но не место их ведения.

              Стоит ДО блока реквизитов намеренно: признак открывает этот блок
              наравне с галочкой согласования, и появляться он должен ниже того,
              чем его включили.

              Отказ «без приложения не выписывается» здесь не предугадывается:
              его говорит СЕРВЕР, и говорит нормой. */}
          <label className="issue__check">
            <input
              type="checkbox"
              checked={excavationChecked}
              onChange={() => patch({ excavationDecision: !excavationChecked })}
              disabled={busy}
            />
            <span>
              Земляные работы в охранной зоне подземных коммуникаций
              <span className="issue__hint">
                Пункт 300 Правил № 222: электрокабели, кабели связи, газопроводы. Такой наряд не выписывается,
                пока к согласованию не приложено письменное разрешение владельца коммуникаций со схемой.
              </span>
            </span>
          </label>
          {proposedExcavation === true && !excavationWaived ? (
            <p className="section__note">
              Справочник видов работ относит эту работу к земляным в охранной зоне.
            </p>
          ) : null}
          {excavationWaived ? (
            <p className="section__note section__note--warn">
              Вы снимаете признак, который предложил справочник. Это будет записано в наряде вместе с вашим именем.
            </p>
          ) : null}

          {/* Issue #468 (B-034): реквизиты согласования. Блок появляется при
              включённом признаке — и только тогда, потому что наряду без
              требования они не нужны.

              Проверок здесь нет: полноту реквизитов и дату не позже выписки
              отвергает СЕРВЕР и отвергает словами. Панель лишь собирает то,
              что ввёл выдающий.

              Issue #469: блок показывается и при одном лишь признаке охранной
              зоны. Пункт 8 Правил № 344 относит к согласуемым ВСЕ земляные
              работы, и прикладывать разрешение иначе было бы некуда. */}
          {approvalChecked || excavationChecked ? (
            <div className="issue__field">
              <span className="issue__label">Согласовано с обслуживающей организацией (пункт 8 Правил)</span>
              <span className="issue__hint">
                Организация, кто согласовал и когда. Задето несколько коммуникаций — добавьте строку на каждую
                организацию.
              </span>
              {draft.safetyApprovals.map((row, index) => (
                <div className="issue__approval" key={index}>
                  <input
                    className="ui-input"
                    value={row.organizationName}
                    onChange={(event) => setApproval(index, { organizationName: event.target.value })}
                    placeholder="Наименование обслуживающей организации"
                    disabled={busy}
                  />
                  <input
                    className="ui-input"
                    value={row.representativeName}
                    onChange={(event) => setApproval(index, { representativeName: event.target.value })}
                    placeholder="ФИО согласовавшего"
                    disabled={busy}
                  />
                  <input
                    className="ui-input"
                    value={row.representativePosition}
                    onChange={(event) => setApproval(index, { representativePosition: event.target.value })}
                    placeholder="Должность согласовавшего"
                    disabled={busy}
                  />
                  {/* Issue #471: ИИН согласовавшего. Выписке он не мешает —
                      наряд выписывается и без него, — но без ИИН нельзя выдать
                      ссылку на подпись графы 8.1: сверять подписанта будет не с
                      чем, и ссылка, попавшая не тому человеку, дала бы чужую
                      подпись под согласованием. Внести его можно и позже, пока
                      графа не подписана. */}
                  <input
                    className="ui-input"
                    value={row.representativeIin ?? ''}
                    onChange={(event) => setApproval(index, { representativeIin: event.target.value })}
                    placeholder="ИИН согласовавшего — нужен, чтобы отправить ему ссылку на подпись"
                    inputMode="numeric"
                    disabled={busy}
                  />
                  <input
                    className="ui-input"
                    type="date"
                    value={row.approvedAt}
                    onChange={(event) => setApproval(index, { approvedAt: event.target.value })}
                    disabled={busy}
                  />
                  {/* Письменное разрешение — необязательно, и об этом сказано
                      вслух: иначе выдающий ищет бумагу, которой может не быть. */}
                  <input
                    className="ui-input"
                    value={row.permitNumber}
                    onChange={(event) => setApproval(index, { permitNumber: event.target.value })}
                    placeholder="Номер письменного разрешения (если выдано)"
                    disabled={busy}
                  />
                  <input
                    className="ui-input"
                    type="date"
                    value={row.permitIssuedAt}
                    onChange={(event) => setApproval(index, { permitIssuedAt: event.target.value })}
                    disabled={busy}
                  />
                  {/* Issue #469: приложение — письменное разрешение владельца
                      коммуникаций и схема с размещением и глубиной заложения
                      (пункт 300 Правил № 222). Файл уходит на сервер сразу при
                      выборе; здесь остаётся то, что видит человек: имя, кто и
                      когда приложил, контрольная сумма. */}
                  <div className="issue__attachments">
                    <span className="issue__hint">
                      Приложенные документы: скан письменного разрешения, схема коммуникаций. PDF или изображение,
                      не больше 10 МБ.
                    </span>
                    {row.attachments.map((attachment) => (
                      <div className="issue__attachment" key={attachment.id}>
                        <span className="issue__attachment-name">{attachment.fileName}</span>
                        <span className="issue__hint">
                          {attachment.uploadedBy.name ?? 'приложил(а)'} ·{' '}
                          {attachment.uploadedAt.slice(0, 10)} · {attachment.checksum}
                        </span>
                        <button
                          type="button"
                          className="ui-btn ui-btn--ghost"
                          onClick={() => detachFile(index, attachment.id)}
                          disabled={busy}
                        >
                          Убрать файл
                        </button>
                      </div>
                    ))}
                    <input
                      type="file"
                      accept={SAFETY_APPROVAL_ATTACHMENT_MIME_TYPES.join(',')}
                      disabled={busy}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        // Поле очищается сразу: иначе второй выбор ТОГО ЖЕ
                        // файла не вызовет onChange, и человек решит, что
                        // приложение не работает.
                        event.target.value = '';
                        if (file) void attachFile(index, file);
                      }}
                    />
                    {attachmentError?.index === index ? (
                      <p className="ui-error" role="alert">
                        Файл не приложен: {attachmentError.message}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="ui-btn ui-btn--ghost"
                    onClick={() => removeApproval(index)}
                    disabled={busy}
                  >
                    Убрать организацию
                  </button>
                </div>
              ))}
              <button type="button" className="ui-btn ui-btn--ghost" onClick={addApproval} disabled={busy}>
                Добавить организацию
              </button>
            </div>
          ) : null}

        </section>

        <section className="issue__block">
          <h3 className="issue__title">Ответственные лица и бригада</h3>

          <PersonField
            label="Ответственный руководитель работ"
            people={responsibleOptions}
            value={draft.supervisorId}
            onChange={(id) => patch({ supervisorId: id })}
            disabled={busy}
          />
          <PersonField
            label="Допускающий"
            hint="Не выбран — допуск оформляет ответственный руководитель работ."
            people={admittingOptions}
            value={draft.admittingUserId}
            onChange={(id) => patch({ admittingUserId: id })}
            disabled={busy}
          />
          <PersonField
            label="Производитель работ"
            people={producerOptions}
            value={draft.producerId}
            onChange={(id) => patch({ producerId: id })}
            disabled={busy}
          />

          <div className="issue__field">
            <span className="issue__label">Состав бригады</span>
            {crew.loading ? <p className="section__note">Загружаю людей подразделения…</p> : null}
            {crew.error ? <p className="ui-error">{crew.error}</p> : null}
            {!crew.loading && !crew.error && people.length === 0 ? (
              <p className="section__note">В вашем подразделении никто не числится.</p>
            ) : null}
            {/* Поиск: людей в организации может быть много. */}
            <input
              className="ui-input issue__crew-search"
              type="search"
              value={crewQuery}
              onChange={(event) => setCrewQuery(event.target.value)}
              placeholder="Фамилия или табельный номер"
              aria-label="Поиск по фамилии или табельному номеру"
            />
            {draft.memberIds.length > 0 ? (
              <span className="issue__hint">Выбрано в бригаду: {draft.memberIds.length}</span>
            ) : null}
            {crewRefusal.size > 0 ? (
              <p className="ui-error">Сервер отказал: бригада не готова. Неготовые подсвечены ниже, причина — у каждого.</p>
            ) : null}
            <ul className="issue__crew">
              {shownCrew.length === 0 && people.length > 0 ? (
                <li className="issue__crew-row">Никого не нашлось.</li>
              ) : null}
              {shownCrew.map((member) => {
                const status = crewStatus(member, crewRequiredGroup);
                const refused = crewRefusal.has(member.id);
                const tone = refused ? 'danger' : status?.tone;
                const opened = openedMemberId === member.id;
                return (
                  <li
                    key={member.id}
                    className={`issue__crew-row${tone ? ` issue__crew-row--${tone}` : ''}${
                      refused ? ' issue__crew-row--refused' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={draft.memberIds.includes(member.id)}
                      onChange={() => toggleMember(member.id)}
                      disabled={busy}
                      aria-label={`В бригаду: ${member.name}`}
                    />
                    {/* Подробности по всем допускам — только по нажатию. */}
                    <button
                      type="button"
                      className="issue__crew-who"
                      onClick={() => setOpenedMemberId(opened ? null : member.id)}
                      aria-expanded={opened}
                    >
                      <span className="issue__crew-name">
                        {member.name} ({member.employeeNumber})
                      </span>
                      {status ? (
                        <span className={`status-word status-word--${status.tone} issue__crew-status`}>{status.text}</span>
                      ) : (
                        <span className="issue__crew-status">основания не загружены</span>
                      )}
                    </button>
                    {refused ? (
                      <span className="issue__crew-refusal">
                        Отказ сервера: {crewRefusal.get(member.id) ?? 'не готов к этой работе'}
                      </span>
                    ) : null}
                    {opened ? (
                      <ul className="issue__crew-details">
                        {crewDetails(member).map((line) => (
                          <li key={line.name}>
                            {line.name}: {line.value}
                          </li>
                        ))}
                        {crewDetails(member).length === 0 ? <li>Допуски не заведены.</li> : null}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          {requiresObserver ? (
            <label className="issue__field">
              <span className="issue__label">Наблюдающий</span>
              <span className="issue__hint">
                Для выбранного вида работы матрица требований называет наблюдающего обязательным. Он выбирается из
                состава бригады.
              </span>
              <select
                className="ui-input"
                value={draft.observerId ?? ''}
                onChange={(event) => patch({ observerId: event.target.value === '' ? null : event.target.value })}
                disabled={busy || draft.memberIds.length === 0}
              >
                <option value="">Не выбран</option>
                {observerOptions.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.employeeNumber})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </section>
      </div>

      {/* Пункт 5 бланка: графы 5.1–5.10 поимённо, каждая со своим номером и
          подстрочным текстом из бланка. Незаполненные остаются пустыми — в
          бланке в них прочерк, а прочерк это отсутствие мероприятия. */}
      <section className="issue__block">
        <h3 className="issue__title">Мероприятия по обеспечению безопасности (пункт 5 бланка)</h3>
        <p className="section__hint">
          Заполняются только нужные графы. Незаполненная графа — прочерк в бланке, а не мероприятие с пустым
          текстом.
        </p>
        <div className="issue__measures">
          {SAFETY_MEASURE_ITEMS.map((item) => {
            const measure = draft.measures[item.number];
            const filled = measure.content.trim().length > 0;
            return (
              <div key={item.number} className="issue__measure">
                <div className="issue__measure-head">
                  <span className="issue__measure-number">{item.number}</span>
                  <span className="issue__measure-title">{item.title}</span>
                </div>
                {item.hint ? <p className="issue__hint">{item.hint}</p> : null}
                {/* #470: почему в графе уже стоит текст. Само мероприятие
                    подставлено выше — здесь объяснение, откуда оно и кого
                    назначать исполнителем. */}
                {servicePresence && item.number === SERVICE_ORG_PRESENCE_ITEM ? (
                  <p className="section__note">
                    По этому виду работ требуется присутствие на месте представителя {servicePresence.party} —
                    мероприятие подставлено в графу. Вызвать представителя обязан выдающий наряд; исполнитель
                    мероприятия подтвердит подписью, что представитель на месте. Не назначите исполнителя — им
                    будет допускающий.
                  </p>
                ) : null}
                <textarea
                  className="ui-input issue__area"
                  value={measure.content}
                  onChange={(event) => setMeasure(item.number, { content: event.target.value })}
                  aria-label={`${item.number} ${item.title}`}
                  disabled={busy}
                />
                {/* Исполнитель мероприятия подписывает свою графу (пункт 7
                    бланка). Необязателен: кто выполнит меру, к моменту
                    выписки известно не всегда. */}
                {filled ? (
                  <label className="issue__field">
                    <span className="issue__label">Исполнитель</span>
                    <select
                      className="ui-input"
                      value={measure.executorUserId ?? ''}
                      onChange={(event) =>
                        setMeasure(item.number, {
                          executorUserId: event.target.value === '' ? null : event.target.value,
                        })
                      }
                      disabled={busy}
                    >
                      <option value="">Не назначен</option>
                      {people.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name} ({member.employeeNumber})
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <div className="issue__submit">
        {/* До нажатия: кто из выбранных в бригаду мешает выписке. Подсказка, а
            не замок — решает сервер. */}
        {blocking.length > 0 ? (
          <div className="ui-error issue__blocking" role="status">
            <strong>Выписать не получится — в бригаде не допущены:</strong>
            <ul>
              {blocking.map(({ member, reasons }) => (
                <li key={member.id}>
                  {member.name} ({member.employeeNumber}) — {reasons}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <button className="ui-btn ui-btn--primary" onClick={() => void issue()} disabled={!complete || busy}>
          {busy ? 'Выписываю…' : 'Выписать наряд'}
        </button>
        {issuedNumber !== null ? (
          <p className="section__note">Наряд № {issuedNumber} выписан. Открыть его можно в разделе «Наряд-допуск».</p>
        ) : null}
      </div>
      {serverRefusal !== null ? (
        <p className="ui-error">Наряд не выписан — причина вверху формы.</p>
      ) : null}
    </div>
  );
}

function emptyDraft(subdivisionId: string | null): IssueDraft {
  return {
    workType: '',
    safetyMeasures: '',
    subdivisionId: subdivisionId ?? '',
    workSiteId: null,
    workSiteDetail: '',
    requiredElectricalGroup: null,
    // undefined, а не null: поле не трогали, и в нём встанет умолчание вида
    // работ, как только загрузится строка матрицы.
    voltageClassDecision: undefined,
    workTypeCatalogId: null,
    supervisorId: null,
    admittingUserId: null,
    producerId: null,
    memberIds: [],
    observerId: null,
    measures: emptySafetyMeasureDrafts(),
    safetyApprovalDecision: null,
    // Issue #468: одна пустая строка — блок появляется уже готовым к
    // заполнению, как только выдающий включит признак согласования.
    safetyApprovals: [emptySafetyApprovalDraft()],
    // Issue #469: галочку не трогали — действует умолчание справочника.
    excavationDecision: null,
  };
}

/**
 * Файл из браузера → строка base64 без префикса `data:`.
 *
 * `FileReader.readAsDataURL` отдаёт «data:application/pdf;base64,JVBERi0…» —
 * префикс снимается здесь, потому что серверу нужны байты, а не URL. Тип файла
 * уходит отдельным полем: в префиксе он тот, что назвал браузер, и вторым
 * источником того же факта делать его незачем.
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Файл «${file.name}» не прочитался`));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      if (comma === -1) {
        reject(new Error(`Файл «${file.name}» не прочитался`));
        return;
      }
      resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Что требует матрица для выбранного вида работы — ПОКАЗЫВАЕТСЯ, а не
 * применяется. Строки приходят с сервера; строки нет — сервер применит
 * базовые требования, и это не ошибка.
 */
function RequirementsPreview({ state, row }: { state: MatrixState; row: WorkRequirementMatrixRow | null }) {
  if (state === 'loading') return <p className="section__note">Смотрю требования к этой работе…</p>;
  if (state === 'missing') {
    return <p className="section__note">Требования для этой работы не заданы — сервер применит базовую проверку.</p>;
  }
  if (state === 'error') return <p className="section__note">Требования не загрузились — их всё равно проверит сервер.</p>;
  if (state !== 'ready' || row === null) return null;
  return (
    <ul className="issue__requirements">
      <li>Наблюдающий: {row.requiresObserver ? 'обязателен' : 'не требуется'}</li>
      <li>Медосмотр: {row.medicalExaminationRequirement === null ? 'не требуется' : 'требуется'}</li>
      {/* «Не задано» — у СТРОКИ МАТРИЦЫ нет умолчания, и это не то же самое,
          что «не указано» в поле наряда ниже: там пусто значение самого
          наряда. Сами классы напряжения называются теми же словами, что и в
          поле, — двух наборов слов на одно перечисление не бывает. */}
      <li>
        Напряжение установки: {row.voltageClass === null ? 'не задано' : voltageLabel(row.voltageClass)}
      </li>
      {/* #470: строка называет, ЧЕЙ представитель. «Требуется присутствие
          представителя» не говорит, кого вызывать, а вызывает его выдающий. */}
      <li>
        Представитель обслуживающей организации на месте:{' '}
        {serviceOrgPresenceRequirement(row)?.party ?? 'не требуется'}
      </li>
    </ul>
  );
}

function PersonField({
  label,
  hint,
  people,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  people: readonly SubdivisionMember[];
  value: string | null;
  onChange: (id: string | null) => void;
  disabled: boolean;
}) {
  return (
    <label className="issue__field">
      <span className="issue__label">{label}</span>
      {hint ? <span className="issue__hint">{hint}</span> : null}
      <select
        className="ui-input"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        disabled={disabled}
      >
        <option value="">Не выбран</option>
        {people.map((member) => (
          <option key={member.id} value={member.id}>
            {member.name} ({member.employeeNumber}) — {crewAdmissionLabel(member) ?? 'основания не загружены'}
          </option>
        ))}
      </select>
      {people.length === 0 ? (
        <span className="issue__hint">
          В подразделении нет никого, кого можно назначить на эту роль: нужны полномочие роли и группа по
          электробезопасности не ниже требуемой.
        </span>
      ) : null}
    </label>
  );
}
